import React, { useEffect, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { base_url } from "../utils/axiosConfig";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { getUserCart, clearCart } from "../features/user/userSlice";
import { toast } from "react-toastify";
import { setAuthenticated, setUser } from "../utils/authAction";
import { LuLogIn } from "react-icons/lu";
import { Modal, Button } from "react-bootstrap";
import { SlActionRedo } from "react-icons/sl";
import { TbCash, TbTruckDelivery, TbCreditCard, TbMapPin } from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import "./Checkout.css";

const validatePincode = async (pincode) => {
  if (!pincode) return false;
  try {
    const response = await axios.get(`${base_url}master/getpin`);
    const pincodedata = response.data;
    return pincodedata.some((item) => item.name.includes(pincode.toString()));
  } catch (error) {
    console.error("Pincode API Error:", error);
    return false;
  }
};

const shippingSchema = yup.object({
  firstName: yup.string().required("First Name is required *"),
  lastName: yup.string().required("Last Name is required *"),
  address: yup.string().required("Address is required *"),
  landmark: yup.string().required("Landmark is required *"),
  city: yup.string().required("City is required *"),
  state: yup.string().required("State is required *"),
  pincode: yup
    .string()
    .required("Pincode is required *")
    .test("is-valid-pincode", "This Pincode is not available", async (value) => await validatePincode(value)),
  phoneNumber: yup
    .string()
    .required("Phone number is required *")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
});

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartProducts, isAuthenticated, user } = useSelector((state) => state.auth);

  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("ONLINE"); // "COD", "ONLINE", "UPI"
  const [loading, setLoading] = useState(false);
  const [useExistingAddress, setUseExistingAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [pincodeError, setPincodeError] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
        if (!token || !isAuthenticated) return;

        const response = await axios.get(`${base_url}user/getshippingadress`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const addresses = response.data.shippingAddresses || [];
        setSavedAddresses(addresses);

        if (addresses.length > 0) {
          setSelectedAddressId(addresses[0]._id);
          setUseExistingAddress(true);
        } else if (user?.address1) {
          formik.setValues({
            ...formik.values,
            address: user.address1,
            firstName: user.firstname || "",
            lastName: user.lastname || "",
            city: "",
            state: "",
            pincode: user.pincode || "",
            landmark: "",
          });
          setUseExistingAddress(false);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error.response?.data || error.message);
        toast.error("Failed to fetch saved addresses.");
      }
    };

    if (isAuthenticated) fetchAddresses();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const token = localStorage.getItem("token") || JSON.parse(localStorage.getItem("customer"))?.token;
    if (token) {
      dispatch(setAuthenticated(true));
      dispatch(setUser({ token }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) dispatch(getUserCart());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const storedTotal = localStorage.getItem("checkoutTotal");
    if (storedTotal) {
      setTotalAmount(parseFloat(storedTotal).toFixed(2));
    } else if (cartProducts?.length > 0) {
      const sum = calculateSubTotal();
      setTotalAmount(sum);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
      const sum = calculateSubTotal(savedCart);
      setTotalAmount(sum);
    }
  }, [cartProducts, isAuthenticated]);

  useEffect(() => {
    if (cartProducts?.length > 0) {
      setCartItems(cartProducts);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
    }
  }, [cartProducts, isAuthenticated]);

  const calculateSubTotal = (items = cartItems) => {
    return items
      .reduce((acc, item) => {
        const price = item?.price || item?.ProductId?.offerPrice || item?.ProductId?.CurrentPrice || 0;
        const gstPercent = parseFloat(item?.ProductId?.gst) || 0;
        const gstAmount = (price * item.quantity * gstPercent) / 100;
        return acc + price * item.quantity + gstAmount;
      }, 0)
      .toFixed(2);
  };

  // Step 1: Create pending order in DB to get orderId
  const createPendingOrder = async (orderData) => {
    const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

    try {
      const response = await axios.post(
        `${base_url}user/cart/create-order`,
        {
          ...orderData,
          paymentInfo: {
            method: "RAZORPAY",
            paymentStatus: "Pending",
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      if (response.data.success && response.data.order?._id) {
        return response.data.order._id;
      }
      toast.error("Failed to create order.");
      return null;
    } catch (error) {
      console.error("Pending order creation failed:", error);
      toast.error(error.response?.data?.message || "Failed to create order.");
      setLoading(false);
      return null;
    }
  };

  // Step 2: Create Razorpay order on backend
  const createRazorpayOrder = async () => {
    const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

    try {
      const response = await axios.post(
        `${base_url}user/razorpay/create`,
        { totalPrice: parseFloat(totalAmount) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.razorpayOrder; // { id, amount, currency, receipt }
    } catch (error) {
      console.error("Razorpay order creation failed:", error);
      toast.error("Failed to initiate payment.");
      setLoading(false);
      return null;
    }
  };

  // Full Razorpay Payment Flow
  const handleRazorpayPayment = async (orderData) => {
    setLoading(true);

    // 1. Create pending order → get orderId
    const orderId = await createPendingOrder(orderData);
    if (!orderId) return;

    // 2. Create Razorpay order
    const razorpayOrder = await createRazorpayOrder();
    if (!razorpayOrder || !razorpayOrder.id) return;

    const options = {
      key: "rzp_live_Sa5Fd59t6WzwoW", // Replace with your live key in production
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency || "INR",
      name: "Shakambari",
      description: "Order Payment",
      order_id: razorpayOrder.id,
      receipt: razorpayOrder.receipt,
      // Force UPI method if selected
      ...(paymentMethod === "UPI" && { method: "upi" }),
      handler: async (response) => {
        try {
          const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");

          // 3. Verify payment
          const verifyResponse = await axios.post(
            `${base_url}user/razorpay/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: orderId,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (verifyResponse.data.success) {
            toast.success("Payment successful! Order confirmed.");
            dispatch(clearCart());
            localStorage.removeItem("cart");
            localStorage.removeItem("checkoutTotal");
            setCartItems([]);
            navigate("/product"); // or your success page
          } else {
            toast.error("Payment verification failed.");
          }
        } catch (error) {
          toast.error("Payment verification error.");
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
      prefill: {
        name: `${formik.values.firstName} ${formik.values.lastName}`,
        email: user?.email || "",
        contact: formik.values.phoneNumber,
      },
      theme: { color: "#3399cc" },
      modal: {
        ondismiss: () => {
          setLoading(false);
          toast.info("Payment cancelled.");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstname || "",
      lastName: user?.lastname || "",
      address: user?.address1 || "",
      landmark: "",
      city: "",
      state: "",
      pincode: user?.pincode || "",
      phoneNumber: user?.phoneNumber || "",
      deliveryInstruction: "",
    },
    validationSchema: useExistingAddress ? null : shippingSchema,
    validateOnChange: !useExistingAddress,
    validateOnBlur: !useExistingAddress,
    onSubmit: () => {},
  });

  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    formik.handleChange(e);
    if (pincode && !useExistingAddress) {
      try {
        const isValid = await validatePincode(pincode);
        setPincodeError(isValid ? null : "We currently do not deliver to this pincode.");
      } catch (error) {
        setPincodeError("Error validating pincode");
      }
    }
  };

  const handleLoginRedirect = () => {
    localStorage.setItem("checkoutFormData", JSON.stringify(formik.values));
    localStorage.setItem("checkoutRedirect", "/checkout");
    if (cartItems?.length > 0) localStorage.setItem("tempCart", JSON.stringify(cartItems));
    window.location.href = "/login";
  };

  useEffect(() => {
    if (isAuthenticated) {
      const savedForm = localStorage.getItem("checkoutFormData");
      const savedCart = localStorage.getItem("tempCart");
      if (savedForm) formik.setValues(JSON.parse(savedForm));
      if (!cartProducts.length && savedCart) setCartItems(JSON.parse(savedCart));
      localStorage.removeItem("checkoutFormData");
      localStorage.removeItem("tempCart");
    }
  }, [isAuthenticated]);

  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      toast.error("Please log in to place order.");
      return;
    }

    if (useExistingAddress && !selectedAddressId) {
      toast.error("Please select a delivery address.");
      return;
    }

    if (!useExistingAddress) {
      await formik.validateForm();
      if (Object.keys(formik.errors).length > 0) {
        toast.error("Please fix the shipping form errors.");
        return;
      }
    }

    setLoading(true);

    const cartItemsToUse = cartItems.length > 0 ? cartItems : JSON.parse(localStorage.getItem("cart")) || [];
    if (cartItemsToUse.length === 0) {
      toast.error("Your cart is empty.");
      setLoading(false);
      return;
    }

    let shippingInfo = useExistingAddress
      ? savedAddresses.find((addr) => addr._id === selectedAddressId)
      : formik.values;

    if (useExistingAddress && shippingInfo) {
      shippingInfo = {
        ...shippingInfo,
        firstName: shippingInfo.firstName || user?.firstname || "",
        lastName: shippingInfo.lastName || user?.lastname || "",
        phoneNumber: formik.values.phoneNumber || user?.phoneNumber || "",
      };
    }

    const orderData = {
      shippingInfo,
      orderItems: cartItemsToUse.map((item) => ({
        product: item.ProductId._id,
        quantity: item.quantity,
        price: item.price,
      })),
      totalPrice: parseFloat(totalAmount),
      useExistingAddress,
      addressId: useExistingAddress ? selectedAddressId : null,
      deliveryInstruction: shippingInfo.deliveryInstruction || "",
      phoneNumber: shippingInfo.phoneNumber || "",
    };

    if (paymentMethod === "COD") {
      const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
      try {
        const response = await axios.post(
          `${base_url}user/cart/create-order`,
          {
            ...orderData,
            paymentInfo: { method: "COD", paymentStatus: "Pending" },
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          toast.success("Order placed successfully!");
          dispatch(clearCart());
          localStorage.removeItem("cart");
          localStorage.removeItem("checkoutTotal");
          setCartItems([]);
          navigate("/product");
        }
      } catch (error) {
        toast.error("Order placement failed.");
      } finally {
        setLoading(false);
      }
    } else {
      // ONLINE or UPI → both use Razorpay
      await handleRazorpayPayment(orderData);
    }
  };

  return (
    <>
      <div className="checkout-container">
        <div className="checkout-wrapper">
          <div className="row g-4">
            {/* Left Section - Shipping Details */}
            <div className="col-lg-8">
              <div className="shipping-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Delivery Address</h2>
                  {!user ? (
                    <button
                      onClick={handleLoginRedirect}
                      className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-rounded shadow-sm"
                    >
                      <span>Login</span>
                      <LuLogIn size={18} />
                    </button>
                  ) : (
                    <div className="welcome-section d-flex align-items-center gap-3">
                      <div className="user-avatar-sm">
                        {user.firstname?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                      </div>
                      <span className="welcome-text">
                        Welcome back, <span className="fw-bold">{user.firstname || 'Customer'}</span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="checkout-stepper">
                  <div className="stepper-line"></div>
                  <div className={`step ${isAuthenticated ? 'completed' : 'active'}`}>
                    <div className="step-icon">1</div>
                    <div className="step-label">Account</div>
                  </div>
                  <div className={`step ${isAuthenticated ? 'active' : ''}`}>
                    <div className="step-icon">2</div>
                    <div className="step-label">Shipping</div>
                  </div>
                  <div className="step">
                    <div className="step-icon">3</div>
                    <div className="step-label">Payment</div>
                  </div>
                </div>

                {!isAuthenticated && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="alert alert-warning border-0 rounded-4 d-flex align-items-center gap-3 mb-4"
                  >
                    <SlActionRedo className="text-warning fs-4" />
                    <div>
                      <p className="mb-0 fw-medium">Please login to continue with shipping</p>
                      <small className="text-muted">Sign in to access your saved addresses and track your order easily.</small>
                    </div>
                  </motion.div>
                )}

                {isAuthenticated && savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
  <h3 className="form-section-title m-0">
    <TbMapPin className="text-primary me-2" />
    Delivery Address
  </h3>

  <div className="form-check custom-checkbox m-0">
    <input
      className="form-check-input"
      type="checkbox"
      id="useExistingAddress"
      checked={useExistingAddress}
      onChange={() => setUseExistingAddress(!useExistingAddress)}
    />
    <label
      className="form-check-label fw-medium ms-2"
      htmlFor="useExistingAddress"
      style={{ cursor: "pointer" }}
    >
      Use one of my saved addresses
    </label>
  </div>
</div>
                    
                    <AnimatePresence>
                      {useExistingAddress && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="address-grid"
                        >
                          {savedAddresses.map((address) => (
                            <motion.div 
                              key={address._id} 
                              whileHover={{ y: -4 }}
                              whileTap={{ scale: 0.98 }}
                              className={`address-item-card ${selectedAddressId === address._id ? 'selected' : ''}`}
                              onClick={() => setSelectedAddressId(address._id)}
                            >
                              <div className="address-chip">Home / Office</div>
                              <div className="address-name">{`${address.firstName || user?.firstname || ""} ${address.lastName || user?.lastname || ""}`}</div>
                              <div className="address-detail">
                                {address.address},<br />
                                {address.landmark && `${address.landmark}, `}{address.city},<br />
                                {address.state} - {address.pincode}
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {(!isAuthenticated || savedAddresses.length === 0 || !useExistingAddress) && (
                  <motion.form
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h3 className="form-section-title">
                      <TbTruckDelivery className="text-primary" /> 
                      {savedAddresses.length > 0 ? 'Enter New Shipping Info' : 'Shipping Information'}
                    </h3>
                    <div className="row g-2">
                      {[
                        { name: "firstName", label: "First Name", placeholder: "e.g. John", col: "col-6" },
                        { name: "lastName", label: "Last Name", placeholder: "e.g. Doe", col: "col-6" },
                        { name: "phoneNumber", label: "Phone Number", placeholder: "10-digit number", col: "col-6" },
                        { name: "pincode", label: "Pincode", placeholder: "6-digit code", onChange: handlePincodeChange, col: "col-6" },
                        { name: "city", label: "City", placeholder: "Enter city", col: "col-6" },
                        { name: "state", label: "State", placeholder: "Enter state", col: "col-6" },
                        { name: "landmark", label: "Landmark / Key Location", placeholder: "Near by...", col: "col-12" },
                        { name: "address", label: "Full Address", placeholder: "House No, Street, Area", col: "col-12" },
                        { name: "deliveryInstruction", label: "Delivery Instructions", placeholder: "Special notes (optional)", col: "col-12", isTextarea: true },
                      ].map(({ name, label, placeholder, onChange, col, isTextarea }) => (
                        <div className={col} key={name}>
                          <div className="input-wrapper-compact">
                            <label className="form-label-compact">{label}</label>
                            {isTextarea ? (
                              <textarea
                                name={name}
                                className="form-input-compact"
                                placeholder={placeholder}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values[name]}
                                rows="2"
                                disabled={!isAuthenticated}
                              />
                            ) : (
                              <input
                                type="text"
                                name={name}
                                className={`form-input-compact ${formik.touched[name] && formik.errors[name] ? 'is-invalid' : ''}`}
                                placeholder={placeholder}
                                onChange={onChange || formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values[name]}
                                disabled={!isAuthenticated}
                              />
                            )}
                            {formik.touched[name] && formik.errors[name] && (
                              <div className="error-text-compact text-danger">{formik.errors[name]}</div>
                            )}
                            {name === "pincode" && pincodeError && <div className="error-text-compact text-danger">{pincodeError}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.form>
                )}
              </div>
            </div>

            {/* Right Section - Order Summary */}
            <div className="col-lg-4">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="order-summary-card"
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Summary</h2>
                  <Link to="/Cart" className="text-primary text-decoration-none d-flex align-items-center small fw-bold">
                    <BiArrowBack className="me-1" /> Edit Cart
                  </Link>
                </div>

                <div className="summary-details bg-light p-3 rounded-4 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="mb-0 text-muted fw-medium">
                      Total Items ({cartItems.reduce((acc, item) => acc + (item.quantity || 0), 0)})
                    </p>
                    <button className="btn btn-sm btn-outline-primary px-3 rounded-pill" onClick={() => setShowCartModal(true)}>
                      View Items
                    </button>
                  </div>
                  <div className="summary-item mb-0">
                    <span className="text-muted">Subtotal</span>
                    <span className="fw-bold">₹{totalAmount}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h5 className="form-section-title mt-0">
                    <TbCreditCard className="text-primary" /> Payment Method
                  </h5>

                  <div className="payment-methods-grid">
                    {/* Pay Online */}
                    <motion.div 
                      whileTap={{ scale: 0.98 }}
                      className={`payment-option-card ${paymentMethod === "ONLINE" ? "selected" : ""} ${(!isAuthenticated || loading) ? "disabled" : ""}`}
                      onClick={() => isAuthenticated && !loading && setPaymentMethod("ONLINE")}
                    >
                      <div className="radio-circle"></div>
                      <img
                        src="https://media.tradly.app/images/marketplace/34521/razor_pay_icon-ICtywSbN.png"
                        alt="Razorpay"
                        className="payment-icon"
                      />
                      <div className="payment-info">
                        <div className="title">Pay Online</div>
                        <div className="subtitle">Cards, Netbanking, Wallets</div>
                      </div>
                    </motion.div>

                    {/* UPI */}
                    <motion.div 
                      whileTap={{ scale: 0.98 }}
                      className={`payment-option-card ${paymentMethod === "UPI" ? "selected" : ""} ${(!isAuthenticated || loading) ? "disabled" : ""}`}
                      onClick={() => isAuthenticated && !loading && setPaymentMethod("UPI")}
                    >
                      <div className="radio-circle"></div>
                      <img 
                        src="https://images.seeklogo.com/logo-png/33/1/unified-payment-interface-upi-logo-png_seeklogo-333088.png"
                        alt="UPI"
                        className="payment-icon"
                      />
                      <div className="payment-info">
                        <div className="title">UPI Payment</div>
                        <div className="subtitle">GPay, PhonePe, Paytm, etc.</div>
                      </div>
                    </motion.div>

                    {/* COD */}
                    <motion.div 
                      whileTap={{ scale: 0.98 }}
                      className={`payment-option-card ${paymentMethod === "COD" ? "selected" : ""} ${(!isAuthenticated || loading) ? "disabled" : ""}`}
                      onClick={() => isAuthenticated && !loading && setPaymentMethod("COD")}
                    >
                      <div className="radio-circle"></div>
                      <div className="payment-icon d-flex align-items-center justify-content-center text-success">
                        <TbCash size={32} />
                      </div>
                      <div className="payment-info">
                        <div className="title">Cash on Delivery</div>
                        <div className="subtitle">Pay when your order arrives</div>
                      </div>
                    </motion.div>
                  </div>

                  {!isAuthenticated && (
                    <motion.div 
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      className="alert alert-soft-danger py-2 mt-2 px-3 rounded-3"
                    >
                      <small className="text-danger fw-bold d-block text-center">
                        Please login to select payment method.
                      </small>
                    </motion.div>
                  )}

                  <div className="summary-total-section mt-auto">
                    <div className="summary-total">
                      <span className="total-label">Total to Pay</span>
                      <h3 className="mb-0 total-amount">₹{totalAmount}</h3>
                    </div>
                    
                    <button
                      className="btn btn-place-order"
                      disabled={!isAuthenticated || loading || (useExistingAddress && !selectedAddressId)}
                      onClick={handlePlaceOrder}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <div className="spinner-border spinner-border-sm" role="status"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        "Confirm & Place Order"
                      )}
                    </button>
                    
                    <p className="text-center text-muted mt-3 small">
                      <TbCreditCard className="me-1" />
                      Secure SSL Encrypted Checkout
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner-custom"></div>
          </div>
        )}

        <Modal show={showCartModal} onHide={() => setShowCartModal(false)} centered size="md">
          <Modal.Header closeButton className="border-bottom">
            <Modal.Title className="h5 mb-0">Your Cart Items</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {cartItems.length > 0 ? (
              cartItems.map((item, i) => (
                <div key={i} className="modal-cart-item">
                  <img
                    src={item?.ProductId?.images?.[0]?.url ? `${base_url}${item.ProductId.images[0].url}` : "/default.jpg"}
                    className="cart-item-img"
                    alt={item?.ProductId?.title}
                  />
                  <div className="item-details">
                    <div className="item-title">{item?.ProductId?.title}</div>
                    <div className="small text-muted">Qty: {item?.quantity}</div>
                    <div className="item-price">₹{item?.price}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-danger py-4">
                <p className="mb-0">Your cart is empty.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top">
            <Button variant="secondary" onClick={() => setShowCartModal(false)}>
              Close
            </Button>
            <Link to="/Cart" className="btn btn-primary">
              Go to Cart
            </Link>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Checkout;